import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import React from "react";
import { AthletesModel } from "../../../util/src/types";
// AthleteRow will be passed as prop to avoid circular dependency
import { CategoryHeader, CategoryHeaderSticky } from "./styled";
import { AthleteRowProps, CategoryRowProps, TableColumn } from "./types";

/**
 * Component for displaying a category header with collapsible athlete rows
 */
const CategoryRow = ({
  categoryKey,
  athletes,
  isCollapsed,
  onToggle,
  categoryColor,
  columns,
  currentAthlete,
  colors,
  AthleteRowComponent,
  onAthleteClick,
}: CategoryRowProps & {
  columns: TableColumn[];
  currentAthlete: AthletesModel | null;
  colors: any;
  AthleteRowComponent: React.ComponentType<AthleteRowProps>;
}) => {
  const [catAge = "", catPoid = ""] = categoryKey.split("|");

  /**
   * Handle category toggle click
   */
  const handleToggle = () => {
    onToggle(categoryKey);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Category header with toggle functionality */}
      <CategoryHeader categorycolor={categoryColor} onClick={handleToggle}>
        <CategoryHeaderSticky
          sx={{
            minWidth: { xs: "110px", sm: "200px" },
          }}
        >
          <IconButton
            size="small"
            sx={{
              minWidth: "auto",
              p: 0.5,
            }}
          >
            {isCollapsed ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ExpandLessIcon fontSize="small" />
            )}
          </IconButton>
          <Typography
            fontWeight="bold"
            noWrap
            sx={{
              flex: 1,
              pl: 1,
              fontSize: {
                xs: "0.9rem",
                sm: "1rem",
                md: "1.1rem",
                lg: "1.2rem",
              },
            }}
          >
            {catPoid} {catAge} ({athletes.length})
          </Typography>
        </CategoryHeaderSticky>
        <Box sx={{ flex: 1 }} />
      </CategoryHeader>

      {/* Collapsible athletes section using MUI Collapse to preserve sticky positioning */}
      <Collapse
        in={!isCollapsed}
        timeout={300}
        sx={{
          // Critical: Don't create stacking context that interferes with sticky positioning
          "& .MuiCollapse-wrapper": {
            // Preserve sticky positioning context
            position: "static !important",
            transform: "none !important",
          },
          "& .MuiCollapse-wrapperInner": {
            // Ensure inner wrapper doesn't interfere
            position: "static !important",
            transform: "none !important",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            // Maintain positioning context for sticky elements
            position: "static",
          }}
        >
          {athletes.map((athlete: AthletesModel) => {
            const isCurrentAthlete = athlete.id === currentAthlete?.id;

            return (
              <AthleteRowComponent
                key={athlete.id}
                athlete={athlete}
                columns={columns}
                isCurrentAthlete={isCurrentAthlete}
                categoryColor={categoryColor}
                colors={colors}
                onRowClick={onAthleteClick}
              />
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CategoryRow;
